export interface DocumentsAndMessagesKeyArguments {
  approvalId: string;
}

/**
 * Hierarchical query-key factory for the aggregated documents & messages read.
 * The shape mirrors `dscWorkbenchKeys` so cache invalidation stays predictable:
 * invalidating `all` flushes every documents-and-messages query, `details()`
 * flushes all reads, and `detail({ approvalId })` flushes a single deal's
 * documents & messages. Never write a literal key array outside this module —
 * the factory IS the schema.
 *
 * Keyed on approvalId alone: the BFF endpoint is
 * `/api/v1/workbench/applications/{appId}/docs-messages`, and the app feeds the
 * route `approvalId` as that `appId` (same identifier used by the sibling
 * `applications/{…}` status and VLA-lock endpoints).
 */
export const documentsAndMessagesKeys = {
  all: ["documentsAndMessages"] as const,
  details: () => [...documentsAndMessagesKeys.all, "detail"] as const,
  detail: (arguments_: DocumentsAndMessagesKeyArguments) =>
    [...documentsAndMessagesKeys.details(), arguments_] as const,
};

export interface DocumentDownloadKeyArguments {
  appId: string;
  docId: string;
}

/**
 * Query-key factory for a single document's presigned download URL (OR-22558).
 * Kept in the same module as the docs-messages keys since both hang off the
 * documents domain. Keyed on `appId` + `docId` — the identifiers the viewer
 * receives over the BroadcastChannel and resolves a fresh URL from.
 */
export const documentDownloadKeys = {
  all: ["docDownloadUrl"] as const,
  detail: (arguments_: DocumentDownloadKeyArguments) => [...documentDownloadKeys.all, arguments_] as const,
};
